'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import classNames from 'classnames';
import { type FC, useActionState } from 'react';
import type { RootState } from '@/app/checkout/(with-tariff-data)/schema';
import { useIsStuck } from '@/components/electricity-tariffs/use-is-stuck';
import type { TariffsQuery } from '@/types/generated';
import styles from './select-cta.module.scss';

const CTA_TEXT = 'Tarif übernehmen';

interface SelectCtaProps {
  product: TariffsQuery['tariffs'][number] | undefined;
  loggedIn: boolean;
  params: RootState;
  trackAddToCart: () => void;
  submitAction: (state: undefined, formData: FormData) => Promise<undefined>;
}

export const SelectCtaPlaceholder = () => {
  const { elementRef, isStuck, isInitialized } = useIsStuck();

  return (
    <>
      <div
        className={classNames(
          styles['select-cta-container'],
          (isStuck || !isInitialized) && styles['select-cta-container-inline'],
        )}
      >
        <form className={styles.form}>
          <BoxCTA className={styles['select-cta']} disabled as="button" size="large">
            {CTA_TEXT}
          </BoxCTA>
        </form>
      </div>
      <div ref={elementRef} />
    </>
  );
};

export const SelectCta: FC<SelectCtaProps> = ({ loggedIn, params, product, trackAddToCart, submitAction }) => {
  const [, dispatch, pending] = useActionState(submitAction, undefined);
  const { elementRef, isStuck, isInitialized } = useIsStuck();

  return (
    <>
      <div
        className={classNames(
          styles['select-cta-container'],
          (isStuck || !isInitialized) && styles['select-cta-container-inline'],
        )}
      >
        <form action={dispatch} className={styles.form} onSubmit={trackAddToCart}>
          <input name="electricityProductCode" type="hidden" value={product?.productCode ?? ''} />
          <Cta
            loggedIn={loggedIn}
            params={params}
            pending={pending}
            product={product}
            trackAddToCart={trackAddToCart}
          />
        </form>
      </div>
      <div ref={elementRef} />
    </>
  );
};

type CtaProps = Omit<SelectCtaProps, 'submitAction'> & { pending: boolean };

export const Cta: FC<CtaProps> = ({ product, pending }) => {
  if (!product) {
    return (
      <BoxCTA className={styles['select-cta']} disabled as="button" size="large">
        {CTA_TEXT}
      </BoxCTA>
    );
  }

  return (
    <BoxCTA
      className={styles['select-cta']}
      ariaLabel={`Tarif ${product.name} auswählen`}
      as="button"
      data-dd-action-name={`Select product: ${product.productCode}`}
      data-testid="button-select-product"
      loading={pending}
      type="submit"
      size="large"
    >
      {CTA_TEXT}
    </BoxCTA>
  );
};
